// /replay — Aufzeichnung des Webinars.
//
// Vorher war /replay eine reine Firebase-Weiterleitung direkt auf die
// Zoom-Cloud-Aufzeichnung. Damit landete der Teilnehmer auf zoom.us, ohne
// jeden Bezug zur Marke und ohne den einzigen Call-to-Action, der nach dem
// Webinar zählt: die Terminbuchung. Jetzt ist /replay eine echte Seite im
// Design des Funnels: Zoom-Recording eingebettet, /termin prominent darüber
// und darunter.
//
// Die Zoom-Aufzeichnung liefert weder X-Frame-Options noch eine
// frame-ancestors-Direktive, das Einbetten per iframe ist also erlaubt.
// Zoom leitet die /rec/share/-URL im Frame selbst auf die /rec/play/-URL
// weiter, deshalb wird hier bewusst der share-Link verwendet (siehe
// WEBINAR_REPLAY_URL in lib/webinar.js). Falls das Embed doch einmal
// blockiert wird (Browser-Einstellung, Tracking-Schutz, Zoom-Änderung),
// steht darunter ein Direktlink auf Zoom.
//
// noindex by default — die Aufzeichnung ist für Webinar-Teilnehmer, nicht
// für den Google-Index.

import React from 'react';
import { Icon } from './Icon.jsx';
import { LogoMark } from './LogoMark.jsx';
import { CookieBanner, AAConsent } from './CookieBanner.jsx';
import { WEBINAR_REPLAY_URL } from '../lib/webinar.js';

function useReplayHead() {
  React.useEffect(() => {
    const prevTitle = document.title;
    document.title = 'AI App Academy · Webinar-Aufzeichnung';

    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) {
      meta.setAttribute(
        'content',
        'Die Aufzeichnung des AI App Academy Webinars: Wie du dir mit KI dein eigenes App-Business baust.'
      );
    }

    let robots = document.querySelector('meta[name="robots"]');
    const hadRobots = !!robots;
    const prevRobots = robots ? robots.getAttribute('content') : null;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    robots.setAttribute('content', 'noindex, nofollow');

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc != null) meta.setAttribute('content', prevDesc);
      if (hadRobots && prevRobots != null) robots.setAttribute('content', prevRobots);
      else if (!hadRobots && robots) robots.remove();
    };
  }, []);
}

export default function WebinarReplayPage() {
  useReplayHead();

  return (
    <>
      <nav className="nav nav-webinar">
        <div className="nav-inner">
          <a href="/" className="logo">
            <div className="logo-mark"><LogoMark size={32} /></div>
            <span className="logo-text">AI App Academy</span>
          </a>
          <a href="/termin" className="btn btn-primary btn-sm nav-cta">
            <span>Infogespräch sichern</span>
            <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
          </a>
        </div>
      </nav>

      <main className="hero webinar-hero replay-hero" id="top">
        <div className="hero-bg-grid" aria-hidden="true"></div>
        <div className="container">
          <div className="webinar-eyebrow">
            <span className="webinar-live-dot"></span>
            <span style={{ textTransform: 'none' }}>WEBINAR-AUFZEICHNUNG</span>
          </div>

          <h1 className="hero-headline webinar-headline replay-headline">
            Hier ist die <em>Aufzeichnung</em>.
          </h1>

          <p className="webinar-sub">
            Wie du dir mit KI dein eigenes App-Business baust. Eine App im Store, die
            Umsatz macht, ohne Code und ohne Entwicklerteam. Schau dir das Webinar in
            Ruhe an und sichere dir danach dein 1:1 Infogespräch.
          </p>

          <div className="replay-cta-top">
            <a href="/termin" className="btn btn-primary replay-cta-btn">
              <span>Jetzt Infogespräch buchen</span>
              <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
            </a>
            <span className="replay-cta-hint">
              <Icon.Calendar size={14} />
              <span>1:1 mit Dominik, kostenlos und unverbindlich</span>
            </span>
          </div>

          <div className="replay-video-wrap">
            <iframe
              className="replay-video-frame"
              src={WEBINAR_REPLAY_URL}
              title="Aufzeichnung: Wie du dir mit KI dein eigenes App-Business baust"
              allow="autoplay; fullscreen; encrypted-media; picture-in-picture"
              allowFullScreen
            />
          </div>

          <div className="replay-video-fallback">
            <span className="replay-video-fallback-hint">Video lädt nicht?</span>
            <a
              href={WEBINAR_REPLAY_URL}
              target="_blank"
              rel="noreferrer"
              className="btn btn-ghost replay-video-fallback-btn"
            >
              <span>Aufzeichnung direkt auf Zoom öffnen</span>
              <Icon.ArrowRight size={16} />
            </a>
          </div>

          <div className="replay-termin-card">
            <div className="replay-termin-icon"><Icon.Calendar size={26} /></div>
            <h2>Dein nächster Schritt: das 1:1 Infogespräch</h2>
            <p>
              Im Webinar hast du den Weg gesehen. Im Infogespräch schauen wir uns
              gemeinsam deine Idee an, klären deinen Use Case und prüfen, ob die App
              Academy die richtige Begleitung für dich ist.
            </p>
            <a href="/termin" className="btn btn-primary replay-termin-btn">
              <span>Termin auf app-academy.ai/termin sichern</span>
              <span className="btn-arrow"><Icon.ArrowRight size={14} /></span>
            </a>
            <span className="replay-termin-note">
              Die Plätze pro Woche sind begrenzt, such dir am besten gleich deinen Slot.
            </span>
          </div>
        </div>
      </main>

      <footer className="webinar-footer">
        <div className="container">
          <div className="webinar-footer-row">
            <div className="webinar-footer-brand">
              <div className="logo-mark"><LogoMark size={24} /></div>
              <span>© 2026 Incibit GmbH · App Academy</span>
            </div>
            <div className="webinar-footer-links">
              <a href="https://incibit.dev/impressum/" target="_blank" rel="noreferrer">Impressum</a>
              <a href="https://incibit.dev/privacy-policy/" target="_blank" rel="noreferrer">Datenschutz</a>
              <a href="https://incibit.dev/agb/" target="_blank" rel="noreferrer">AGB</a>
              <a
                href="#cookie-settings"
                onClick={(e) => { e.preventDefault(); AAConsent.openSettings(); }}
              >
                Cookie-Einstellungen
              </a>
            </div>
          </div>
        </div>
      </footer>

      <CookieBanner />
    </>
  );
}
